import type { JudgeConfig } from '@meridian-ssa/shared';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { apiRequest } from '@/config';

export default function JudgeConfigsPage() {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState<JudgeConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    loadConfigs();
  }, []);

  async function loadConfigs() {
    try {
      setLoading(true);
      setError(null);
      const response = await apiRequest('/api/judge-configs');
      setConfigs(response.configs);
    } catch (err: any) {
      setError(err.message || 'Failed to load judge configs');
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id: string, name: string) {
    if (
      !confirm(
        `Are you sure you want to delete "${name}"?\n\nThis will fail if any votes reference this judge config.`
      )
    ) {
      return;
    }

    try {
      setDeletingId(id);
      setError(null);
      await apiRequest(`/api/judge-configs/${id}`, { method: 'DELETE' });
      // Remove from local state
      setConfigs(configs.filter(c => c.id !== id));
    } catch (err: any) {
      setError(err.message || 'Failed to delete judge config');
    } finally {
      setDeletingId(null);
    }
  }

  function getProviderBadgeColor(provider: string) {
    switch (provider) {
      case 'openai':
        return 'bg-green-100 text-green-800';
      case 'anthropic':
        return 'bg-purple-100 text-purple-800';
      case 'xai':
        return 'bg-blue-100 text-blue-800';
      case 'gemini':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center text-gray-600">
          Loading judge configs...
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Judge Configs</h1>
          <p className="text-gray-600 mt-1">
            Manage LLM-as-a-judge configurations with custom prompts and models
          </p>
        </div>
        <button
          onClick={() => navigate('/judge-configs/create')}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Create New Judge
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {configs.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded">
          <p className="text-gray-600 mb-4">No judge configs yet</p>
          <button
            onClick={() => navigate('/judge-configs/create')}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Create Your First Judge
          </button>
        </div>
      ) : (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Provider
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Model
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Config
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {configs.map(config => (
                <tr key={config.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {config.name}
                    </div>
                    {config.created_by && (
                      <div className="text-xs text-gray-500">
                        by {config.created_by}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${getProviderBadgeColor(config.provider)}`}
                    >
                      {config.provider}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {config.api_model}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    temp: {config.temperature} | max: {config.max_tokens}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(config.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleDelete(config.id, config.name)}
                      disabled={deletingId === config.id}
                      className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {deletingId === config.id ? 'Deleting...' : 'Delete'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded">
        <h3 className="font-semibold text-blue-900 mb-2">Note</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Judge configs are immutable once created (cannot be edited)</li>
          <li>• Configs can only be deleted if no votes reference them</li>
          <li>
            • All prompts must include: {'{{'} ORIGINAL_PROMPT{'}}'}, {'{{'}{' '}
            RESULT_A{'}}'}, {'{{'} RESULT_B{'}}'}
          </li>
        </ul>
      </div>
    </div>
  );
}
